import React, { useState, useEffect } from "react";
import { getTestRun } from "../api/testRuns.js";
import TranscriptView from "../components/TranscriptView.jsx";
import ToolTraceView from "../components/ToolTraceView.jsx";
import VerificationBadge from "../components/VerificationBadge.jsx";

export default function RunDetail({ navigate, runId }) {
  const [run, setRun] = useState(null);
  const [tab, setTab] = useState("transcript");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!runId) return;
    getTestRun(runId).then(setRun).catch(console.error).finally(() => setLoading(false));
  }, [runId]);

  const [copied, setCopied] = useState(false);

  const copyContext = () => {
    if (!run) return;
    const lines = [];

    // Header
    lines.push(`TEST: ${run.testCaseName || run.id}`);
    lines.push(`RUN: ${run.runCode || run.id} | STATUS: ${run.status} | DURATION: ${run.duration}s`);
    lines.push(`PATIENT: ${run.patientName} (${run.patientId})`);
    lines.push(`CONFIG: ${run.configMode || "draft"}`);
    lines.push("");

    // Transcript — handle both bridge format and server format
    lines.push("═══ CONVERSATION ═══");
    (run.transcript || []).forEach((t) => {
      if (!t.text) return;
      const role = t.role === "agent" ? "SANDY" : t.role.toUpperCase();
      const agentLabel = t.agent ? ` [${t.agent}]` : "";
      lines.push(`[${role}${agentLabel}]: ${t.text}`);
    });
    lines.push("");

    // Tool calls — compact, strip _agent_context and agent_instructions
    lines.push("═══ TOOL CALLS ═══");
    (run.toolCalls || []).forEach((tc) => {
      const cleanResult = { ...tc.result };
      delete cleanResult._agent_context;
      delete cleanResult.agent_instructions;
      const resultStr = JSON.stringify(cleanResult);
      const trimmed = resultStr.length > 400 ? resultStr.slice(0, 400) + "..." : resultStr;
      lines.push(`TOOL: ${tc.name}(${JSON.stringify(tc.args)}) → ${trimmed}`);
    });
    lines.push("");

    // Verification — only failures and issues
    if (run.verification) {
      const v = run.verification;
      lines.push("═══ VERIFICATION ═══");
      lines.push(`RESULT: ${v.passed ? "PASSED" : "FAILED"} (score: ${v.score})`);
      if (v.summary) lines.push(`SUMMARY: ${v.summary}`);
      const failed = (v.criteria || []).filter((c) => !c.passed);
      if (failed.length > 0) {
        lines.push("");
        lines.push("FAILED CRITERIA:");
        failed.forEach((c) => {
          lines.push(`  ✗ ${c.name}: ${c.details}`);
        });
      }
      if ((v.issues || []).length > 0) {
        lines.push("");
        lines.push("ISSUES:");
        v.issues.forEach((issue) => {
          lines.push(`  - ${typeof issue === "string" ? issue : issue.description || JSON.stringify(issue)}`);
        });
      }
    }

    navigator.clipboard.writeText(lines.join("\n")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return <div style={s.loading}>Loading...</div>;
  if (!run) return <div style={s.loading}>Run not found</div>;

  const tabs = [
    { key: "transcript", label: "Transcript" },
    { key: "tools", label: `Tool Calls (${run.toolCalls?.length || 0})` },
    { key: "verification", label: "Verification" },
    { key: "prompt", label: "Patient Prompt" },
  ];

  return (
    <div>
      {/* Header */}
      <div style={s.header}>
        <button style={s.back} onClick={() => navigate("history")}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <div style={s.headerInfo}>
          <h1 style={s.title}>{run.testCaseName || run.id}</h1>
          <div style={s.meta}>
            <span>{run.id}</span>
            <span style={s.metaDot}>&middot;</span>
            <span>{run.patientName} ({run.patientId})</span>
            <span style={s.metaDot}>&middot;</span>
            <span>{run.duration}s</span>
            <span style={s.metaDot}>&middot;</span>
            <span style={run.status === "completed" ? s.statusOk : s.statusErr}>{run.status}</span>
          </div>
        </div>
        <button style={copied ? s.copyBtnDone : s.copyBtn} onClick={copyContext}>
          {copied ? "Copied!" : "Copy Context"}
        </button>
        <div style={s.headerBadge}>
          <VerificationBadge verification={run.verification} size="lg" />
        </div>
      </div>

      {/* Audio Player — text mode has no audio */}

      {/* Tabs */}
      <div style={s.tabBar}>
        {tabs.map((t) => (
          <button
            key={t.key}
            style={{ ...s.tab, ...(tab === t.key ? s.tabActive : {}) }}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={s.content}>
        {tab === "transcript" && <TranscriptView transcript={run.transcript} />}
        {tab === "tools" && <ToolTraceView toolCalls={run.toolCalls} />}
        {tab === "verification" && <VerificationView verification={run.verification} />}
        {tab === "prompt" && <PromptView prompt={run.patientPrompt} />}
      </div>
    </div>
  );
}

function VerificationView({ verification }) {
  if (!verification) return <div style={s.emptyContent}>No verification data available.</div>;

  const { passed, score, summary, criteria, issues } = verification;

  return (
    <div>
      {/* Header */}
      <div style={s.verHeader}>
        <VerificationBadge verification={verification} size="lg" />
        {score != null && <span style={s.score}>Score: {score}/100</span>}
      </div>

      {/* Summary */}
      {summary && <p style={s.summary}>{summary}</p>}

      {/* Criteria */}
      {Array.isArray(criteria) && criteria.length > 0 && (
        <div style={s.criteriaList}>
          {criteria.map((c, i) => (
            <div key={i} style={s.criterion}>
              <span style={c.passed ? s.checkPass : s.checkFail}>
                {c.passed ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
                )}
              </span>
              <div>
                <div style={s.criterionName}>{c.name || `Criterion ${i + 1}`}</div>
                <div style={s.criterionDetail}>{c.details || ""}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Issues */}
      {Array.isArray(issues) && issues.length > 0 && (
        <div style={s.issuesSection}>
          <div style={s.issuesTitle}>Issues</div>
          {issues.map((issue, i) => (
            <div key={i} style={s.issue}>{typeof issue === "string" ? issue : JSON.stringify(issue)}</div>
          ))}
        </div>
      )}

      {/* Raw data fallback — show full verification object if criteria is missing */}
      {(!Array.isArray(criteria) || criteria.length === 0) && !summary && (
        <div>
          <div style={s.issuesTitle}>Raw Verification Data</div>
          <pre style={s.pre}>{JSON.stringify(verification, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

function PromptView({ prompt }) {
  return <pre style={s.pre}>{prompt || "No prompt data"}</pre>;
}

const s = {
  loading: { padding: 60, textAlign: "center", color: "#999", fontSize: 14 },
  header: {
    display: "flex", alignItems: "flex-start", gap: 12,
    padding: "20px 32px", borderBottom: "1px solid #e5e5e5",
  },
  back: {
    width: 32, height: 32, border: "1px solid #e5e5e5", borderRadius: 8,
    background: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
    color: "#666", flexShrink: 0, marginTop: 2,
  },
  headerInfo: { flex: 1 },
  title: { fontSize: 18, fontWeight: 600, letterSpacing: "-0.02em", margin: 0 },
  meta: { fontSize: 13, color: "#999", marginTop: 4, display: "flex", alignItems: "center", gap: 6 },
  metaDot: { color: "#ddd" },
  statusOk: { color: "#059669", fontWeight: 500 },
  statusErr: { color: "#dc2626", fontWeight: 500 },
  copyBtn: {
    padding: "6px 14px", background: "#fff", color: "#0f0f0f", border: "1px solid #e5e5e5",
    borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap",
  },
  copyBtnDone: {
    padding: "6px 14px", background: "#ecfdf5", color: "#059669", border: "1px solid #a7f3d0",
    borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "default", whiteSpace: "nowrap",
  },
  headerBadge: { marginLeft: "auto", flexShrink: 0 },
  audioBar: {
    display: "flex", alignItems: "center", gap: 12,
    padding: "12px 32px", borderBottom: "1px solid #e5e5e5", background: "#fafafa",
  },
  audioLabel: { fontSize: 13, fontWeight: 500, color: "#666", whiteSpace: "nowrap" },
  audio: { flex: 1, height: 32 },
  tabBar: {
    display: "flex", gap: 0, padding: "0 32px",
    borderBottom: "1px solid #e5e5e5",
  },
  tab: {
    padding: "12px 16px", border: "none", background: "transparent",
    fontSize: 13, fontWeight: 500, color: "#999",
    borderBottom: "2px solid transparent", marginBottom: -1,
  },
  tabActive: { color: "#0f0f0f", borderBottomColor: "#0f0f0f" },
  content: { padding: "20px 32px 32px" },
  emptyContent: { color: "#999", padding: 20, textAlign: "center" },
  verHeader: { display: "flex", alignItems: "center", gap: 12, marginBottom: 16 },
  score: { fontSize: 15, fontWeight: 600, color: "#333" },
  summary: { fontSize: 14, color: "#555", marginBottom: 20, lineHeight: 1.5 },
  criteriaList: {
    border: "1px solid #e5e5e5", borderRadius: 10, overflow: "hidden",
  },
  criterion: {
    display: "flex", gap: 10, padding: "12px 16px",
    borderBottom: "1px solid #f0f0f0", alignItems: "flex-start",
  },
  checkPass: { color: "#059669", flexShrink: 0, marginTop: 1 },
  checkFail: { color: "#dc2626", flexShrink: 0, marginTop: 1 },
  criterionName: { fontSize: 14, fontWeight: 500, color: "#0f0f0f" },
  criterionDetail: { fontSize: 13, color: "#666", marginTop: 2, lineHeight: 1.4 },
  issuesSection: { marginTop: 20 },
  issuesTitle: { fontSize: 14, fontWeight: 600, marginBottom: 8 },
  issue: {
    fontSize: 13, color: "#dc2626", padding: "6px 12px", background: "#fef2f2",
    borderRadius: 6, marginBottom: 4,
  },
  pre: {
    background: "#fafafa", padding: 20, borderRadius: 10, fontSize: 13,
    lineHeight: 1.6, whiteSpace: "pre-wrap", overflow: "auto", maxHeight: 500,
    fontFamily: "'SF Mono', Monaco, 'Cascadia Code', monospace",
    border: "1px solid #e5e5e5", color: "#333",
  },
};
